import {
  $generateNodesFromSerializedNodes,
  $getLexicalContent,
  $insertGeneratedNodes,
} from "@lexical/clipboard";
import {
  $isParentRTL,
  $shouldOverrideDefaultCharacterSelection,
  $moveCharacter,
} from "@lexical/selection";
import {
  $getAdjacentNode,
  $getRoot,
  $getSelection,
  $isDecoratorNode,
  $isNodeSelection,
  $isRangeSelection,
  BaseSelection,
  BLUR_COMMAND,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  CUT_COMMAND,
  FOCUS_COMMAND,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_DOWN_COMMAND,
  KEY_ENTER_COMMAND,
  LexicalEditor,
  PASTE_COMMAND,
  RangeSelection,
} from "lexical";
import { useEffect, useRef, useState } from "react";
import { mergeRegister } from "./editor-utils";
import { hotkeyManager } from "../SkedCaption/hot-key-manager";

export const useEventCommands = (
  editor: LexicalEditor,
  options: { onEnterSubmit?: () => void }
) => {
  /** must use ref to get latest options */
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        FOCUS_COMMAND,
        () => {
          setIsFocused(true);
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        BLUR_COMMAND,
        () => {
          setIsFocused(false);
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (e) => {
          if (
            optionsRef.current.onEnterSubmit &&
            e?.key === "Enter" &&
            !e?.shiftKey &&
            !e?.altKey &&
            !e?.ctrlKey
          ) {
            e.preventDefault();
            optionsRef.current.onEnterSubmit();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (e) => {
          if (hotkeyManager.isSubmitPostHotkey(e)) {
            e.preventDefault();
            return true;
          }

          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        () => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            selection.clear();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        KEY_ARROW_UP_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            // If selection is on a node, let's try and move selection
            // back to being a range selection.
            const nodes = selection.getNodes();
            if (nodes.length > 0) {
              nodes[0].selectPrevious();
              return true;
            }
          } else if ($isRangeSelection(selection)) {
            const possibleNode = $getAdjacentNode(selection.focus, true);
            if (
              !event.shiftKey &&
              $isDecoratorNode(possibleNode) &&
              !possibleNode.isIsolated() &&
              !possibleNode.isInline()
            ) {
              possibleNode.selectPrevious();
              event.preventDefault();
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        KEY_ARROW_DOWN_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            // If selection is on a node, let's try and move selection
            // back to being a range selection.
            const nodes = selection.getNodes();
            if (nodes.length > 0) {
              nodes[0].selectNext(0, 0);
              return true;
            }
          } else if ($isRangeSelection(selection)) {
            if ($isSelectionAtEndOfRoot(selection)) {
              event.preventDefault();
              return true;
            }
            const possibleNode = $getAdjacentNode(selection.focus, false);
            if (
              !event.shiftKey &&
              $isDecoratorNode(possibleNode) &&
              !possibleNode.isIsolated() &&
              !possibleNode.isInline()
            ) {
              possibleNode.selectNext();
              event.preventDefault();
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        KEY_ARROW_LEFT_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            // If selection is on a node, let's try and move selection
            // back to being a range selection.
            const nodes = selection.getNodes();
            if (nodes.length > 0) {
              event.preventDefault();
              if ($isParentRTL(nodes[0])) {
                nodes[0].selectNext(0, 0);
              } else {
                nodes[0].selectPrevious();
              }
              return true;
            }
          }
          if (!$isRangeSelection(selection)) {
            return false;
          }
          if ($shouldOverrideDefaultCharacterSelection(selection, true)) {
            const isHoldingShift = event.shiftKey;
            event.preventDefault();
            $moveCharacter(selection, isHoldingShift, true);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        KEY_ARROW_RIGHT_COMMAND,
        (event: KeyboardEvent) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            // If selection is on a node, let's try and move selection
            // back to being a range selection.
            const nodes = selection.getNodes();
            if (nodes.length > 0) {
              event.preventDefault();
              if ($isParentRTL(nodes[0])) {
                nodes[0].selectPrevious();
              } else {
                nodes[0].selectNext(0, 0);
              }
              return true;
            }
          }
          if (!$isRangeSelection(selection)) {
            return false;
          }
          const isHoldingShift = event.shiftKey;
          if ($shouldOverrideDefaultCharacterSelection(selection, false)) {
            event.preventDefault();
            $moveCharacter(selection, isHoldingShift, false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        COPY_COMMAND,
        (ev) => {
          if (ev instanceof ClipboardEvent) {
            ev.clipboardData?.setData(
              "application/x-lexical-editor",
              $getLexicalContent(editor, $getSelection()) || ""
            );
          }
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CUT_COMMAND,
        (ev) => {
          if (ev instanceof ClipboardEvent) {
            ev.clipboardData?.setData(
              "application/x-lexical-editor",
              $getLexicalContent(editor, $getSelection()) || ""
            );
          }
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (ev) => {
          if (ev instanceof ClipboardEvent && ev.clipboardData) {
            // Paste with the method for rich text editors
            // This works without issue because we never try to insert nodes not previously registered
            const selection = $getSelection();
            if (selection) {
              $insertDataTransferForSkedEditor(
                ev.clipboardData,
                selection,
                editor
              );
              ev.preventDefault(); // Unsure if necessary
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_CRITICAL // Not necessary. Ensures it is run before the internal handler.
      )
    );
  }, [editor]);
  return { isFocused };
};

function $isSelectionAtEndOfRoot(selection: RangeSelection) {
  const focus = selection.focus;
  return focus.key === "root" && focus.offset === $getRoot().getChildrenSize();
}

function $insertDataTransferForSkedEditor(
  dataTransfer: DataTransfer,
  selection: BaseSelection,
  editor: LexicalEditor
) {
  const lexicalString = dataTransfer.getData("application/x-lexical-editor");

  if (lexicalString) {
    try {
      const payload = JSON.parse(lexicalString);
      if (
        payload.namespace === editor._config.namespace &&
        Array.isArray(payload.nodes)
      ) {
        const nodes = $generateNodesFromSerializedNodes(payload.nodes);
        return $insertGeneratedNodes(editor, nodes, selection);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
  const plainString = dataTransfer.getData("text/plain");
  try {
    if (plainString) {
      selection.insertRawText(plainString);
    }
  } catch (error) {
    console.error(error);
  }
}
